/**
 * Sending events.
 *
 * Three rules, and they are the whole design:
 *
 * 1. **Nothing identifying is ever sent from here.** No visitor id, no device id, no user id.
 *    The API already knows who is calling from the session it resolved, and it derives the rest
 *    from the request. A client that cannot send an identifier cannot leak one.
 * 2. **Never on the critical path.** Events are buffered and flushed on a timer, and a failed
 *    flush is dropped rather than retried into a loop.
 * 3. **Never throws.** A screen that breaks because analytics broke is worse than no analytics.
 */

import {
  ANALYTICS_BATCH_MAX,
  CLIENT_PLATFORM_HEADER,
  type AnalyticsEvent,
  type AnalyticsEventName,
} from '@goodbuild/shared';
import { API_URL, getBearerToken } from '../api/client.js';
import { isNative, platform } from '../platform/index.js';

/** Long enough to batch a burst of navigation, short enough that a closed tab loses little. */
const FLUSH_MS = 5_000;

/**
 * The most events held before a flush is forced.
 *
 * Matches the server's batch ceiling, so the queue never builds a request that would be
 * rejected whole.
 */
const MAX_QUEUED = ANALYTICS_BATCH_MAX;

/**
 * Where unsent events wait for a network.
 *
 * The desktop app is offline regularly — a laptop on a train still plays games — and a finished
 * play session that never reaches the API is playtime that simply never happened as far as the
 * dashboard is concerned. The API believes a client's own timestamp for up to a fortnight, so a
 * session flushed two days late still lands on the day it was played.
 *
 * `localStorage` rather than a file through Tauri, because it is the one store both the website
 * and the desktop webview already have. It goes when somebody clears their data, which is the
 * correct outcome for it.
 */
const OUTBOX_KEY = 'gb.analytics.outbox';

/**
 * The most events kept across restarts.
 *
 * Bounded because an outbox that grows without a ceiling on a machine that never comes back
 * online is a slow leak with somebody's browser storage quota at the end of it. Oldest go first.
 */
const OUTBOX_MAX = 200;

let queue: AnalyticsEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let enabled = true;
let started = false;

function readOutbox(): AnalyticsEvent[] {
  try {
    const raw = localStorage?.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AnalyticsEvent[]) : [];
  } catch {
    return [];
  }
}

function writeOutbox(events: AnalyticsEvent[]): void {
  try {
    if (events.length === 0) localStorage?.removeItem(OUTBOX_KEY);
    else localStorage?.setItem(OUTBOX_KEY, JSON.stringify(events.slice(-OUTBOX_MAX)));
  } catch {
    // A full or blocked store costs the history, not the screen.
  }
}

/** Fields a caller may set. The rest of the event is filled in here. */
export interface TrackOptions {
  route?: string;
  gameId?: string;
  value?: number;
  detail?: string;
}

/**
 * Detail labels are constrained server-side, so they are cleaned here rather than rejected
 * there: an error message that arrives as a sentence becomes a slug, and an event that would
 * have been dropped for the shape of one field is kept.
 */
function cleanDetail(detail: string | undefined): string | undefined {
  if (!detail) return undefined;
  const slug = detail
    .toLowerCase()
    .replace(/[^\w.:-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
  return slug.length > 0 ? slug : undefined;
}

export function track(name: AnalyticsEventName, options: TrackOptions = {}): void {
  if (!enabled) return;
  try {
    queue.push({
      t: name,
      ts: Date.now(),
      ...(options.route ? { route: options.route } : {}),
      ...(options.gameId ? { gameId: options.gameId } : {}),
      ...(Number.isFinite(options.value) ? { value: options.value } : {}),
      ...(cleanDetail(options.detail) ? { detail: cleanDetail(options.detail) } : {}),
    });

    if (queue.length >= MAX_QUEUED) {
      void flush();
      return;
    }
    if (timer === null) timer = setTimeout(() => void flush(), FLUSH_MS);
  } catch {
    // A full queue or a frozen object costs the event, not the screen.
  }
}

/**
 * Sends whatever is queued.
 *
 * `keepalive` rather than `sendBeacon`: a beacon cannot set the platform header and cannot carry
 * the session cookie's credentials mode, so it would arrive as an anonymous event from an
 * unknown platform — which is most of what makes an event worth having. `keepalive` keeps both
 * and still survives the page being closed.
 */
export async function flush(): Promise<void> {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0 || !enabled) return;

  const events = queue.slice(0, MAX_QUEUED);
  queue = queue.slice(MAX_QUEUED);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      [CLIENT_PLATFORM_HEADER]: platform,
    };
    const bearer = getBearerToken();
    if (isNative && bearer) headers.Authorization = `Bearer ${bearer}`;

    const response = await fetch(`${API_URL.replace(/\/$/, '')}/v1/e`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events }),
      credentials: isNative ? 'omit' : 'include',
      keepalive: true,
    });

    // A 4xx means this batch will never be accepted, however many times it is sent. Only a
    // network failure or a server error is worth keeping, which is what stops a single malformed
    // event from being retried until the end of time.
    if (!response.ok && response.status >= 500) keep(events);
  } catch {
    // Offline, or the tab went away mid-flight. Both are worth another try.
    keep(events);
  }
}

/** Puts a failed batch back, oldest first, inside the ceiling. */
function keep(events: AnalyticsEvent[]): void {
  writeOutbox([...readOutbox(), ...events]);
}

/**
 * Sends whatever was left over from a previous run.
 *
 * Runs once at startup and again whenever the machine comes back online. Cleared from storage
 * before the send rather than after: a crash mid-flush should lose a batch, not replay it on
 * every start forever.
 */
async function drainOutbox(): Promise<void> {
  const pending = readOutbox();
  if (pending.length === 0 || !enabled) return;
  writeOutbox([]);
  queue = [...pending, ...queue];
  await flush();
}

/**
 * Starts the flush-on-hide listeners.
 *
 * `visibilitychange` rather than `unload`, which does not fire reliably anywhere any more and
 * never fires on mobile. `pagehide` covers the back/forward cache.
 */
export function startAnalytics(): void {
  if (started || typeof document === 'undefined') return;
  started = true;

  const onHide = (): void => {
    if (document.visibilityState === 'hidden') void flush();
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', () => void flush());
  window.addEventListener('online', () => void drainOutbox());

  void drainOutbox();
}

/**
 * Turns collection off for the rest of the session, and drops anything queued.
 *
 * Wired to the setting on the privacy screen. It is deliberately not persisted here — the
 * setting is, and it calls this on load.
 */
export function setAnalyticsEnabled(next: boolean): void {
  enabled = next;
  if (!next) {
    queue = [];
    // The outbox goes too. Turning this off and leaving a fortnight of play sessions on disk to
    // be uploaded the next time it is turned on would make the setting a pause button, and it
    // is not described as one.
    writeOutbox([]);
  }
}

export function analyticsEnabled(): boolean {
  return enabled;
}
